import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { AdminBlockResponse } from "../contracts/AdminBlockResponse";
import { AdminReportCountResponse } from "../contracts/AdminReportCountResponse";
import { AdminReportResponse, AdminReportStatus } from "../contracts/AdminReportResponse";
import { BlockReportedRequest } from "../contracts/BlockReportedRequest";
import { CreateBlockRequest } from "../contracts/CreateBlockRequest";
import { PaginatedAdminReportResult } from "../contracts/PaginatedAdminReportResult";

type Rejected = { rejectValue: ApiErrorResponse };

/** Reports newest first (US-111); `status` null lists every status. */
export const fetchAdminReports = createAsyncThunk<
  PaginatedAdminReportResult,
  { status: AdminReportStatus | null; page: number; pageSize: number },
  Rejected
>("admin/fetchReports", async ({ status, page, pageSize }, { rejectWithValue }) => {
  try {
    const params = { page, pageSize, ...(status ? { status } : {}) };
    const { data } = await axiosInstance.get<PaginatedAdminReportResult>("/admin/reports", { params });
    return data;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load reports"));
  }
});

/** The open reports behind the Reports tab badge. */
export const fetchAdminReportCount = createAsyncThunk<number, void, Rejected>(
  "admin/fetchReportCount",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get<AdminReportCountResponse>("/admin/reports/count");
      return data.open;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to count reports"));
    }
  },
);

const reportAction = (name: string, path: string) =>
  createAsyncThunk<AdminReportResponse, string, Rejected>(`admin/${name}`, async (reportId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post<AdminReportResponse>(`/admin/reports/${reportId}/${path}`);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to handle the report"));
    }
  });

/** Closes a report with no action. */
export const dismissAdminReport = reportAction("dismissReport", "dismiss");

/** Deletes the reported message for everyone (the existing delete) and marks the report actioned. */
export const deleteReportedMessage = reportAction("deleteReportedMessage", "delete-message");

/** Blocks the person behind a report and marks it actioned. */
export const blockReported = createAsyncThunk<AdminReportResponse, { reportId: string } & BlockReportedRequest, Rejected>(
  "admin/blockReported",
  async ({ reportId, reason }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post<AdminReportResponse>(`/admin/reports/${reportId}/block`, { reason });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to block"));
    }
  },
);

/** Blocks a user account or a member directly (Users and Members pages). */
export const createAdminBlock = createAsyncThunk<AdminBlockResponse, CreateBlockRequest, Rejected>(
  "admin/createBlock",
  async (request, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post<AdminBlockResponse>("/admin/blocks", request);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to block"));
    }
  },
);

/** Lifts a block; the row stays as a record with when and by whom. */
export const removeAdminBlock = createAsyncThunk<AdminBlockResponse, string, Rejected>(
  "admin/removeBlock",
  async (blockId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.delete<AdminBlockResponse>(`/admin/blocks/${blockId}`);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to unblock"));
    }
  },
);
